import { observer } from 'mobx-react';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../../../utils/api';
import UIStore from '../../../stores/UIStore';
import { Price, RichTextEditor, SafeHtml } from '@partsbadger/library';
import { ContactDetailProps } from '@partsbadger/types';

import {
    Alert,
    Breadcrumb,
    Button,
    Card,
    Col,
    Drawer,
    List,
    message,
    Modal,
    notification,
    Row,
    Skeleton,
    Typography,
    Tooltip,
    Spin,
} from 'antd';
import { useHistory } from 'react-router-dom';
import AdditionalRequirements from '../../../components/shared/AdditionalRequirements';
import ErrorRender from '../../../components/shared/ErrorRender';
import { AllDynamicQuantities, PartDimensions, PartPreview, RequestNewQuote } from '../../../components/Quotes';

import QuoteDetailHeader from '../../../components/Quotes/QuoteDetailHeader';

import QuoteStore from '../../../stores/QuoteStore';
import { AdditionalRequirementsProps, QuoteLineItemProps, QuoteProps } from '../../../types/types';
import _ from 'lodash';

import { ArrowRightOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import UserStore from '../../../stores/UserStore';
import SalesOrderStore from '../../../stores/SalesOrderStore';
import { getCurrentUser } from '../../../hooks/userHook';
import PortalLoading from '../../../components/PortalLoading';

import { LineItemsGroup } from './LineItemsGroup';
const { Title } = Typography;

interface IProps {
    match: {
        params: {
            id: number;
        };
    };
}

const BreadcrumbsNav: React.FC<{ quote: QuoteProps }> = ({ quote }) => {
    const links = [
        { title: 'Quote List', link: '/my-account/quotes/' },
        { title: quote?.original_quote ?? 'Quote Detail', link: `/my-account/quotes/${quote?.duplicated_from}/` },
        { title: `Revision ${quote.revision}`, link: null },
    ];

    return (
        <Row className="container">
            <Breadcrumb>
                {links.map(link => (
                    <Breadcrumb.Item key={link.title} href={link.link ?? ''}>
                        {link.title}
                    </Breadcrumb.Item>
                ))}
            </Breadcrumb>
        </Row>
    );
};

const QuoteNotes: React.FC<{
    noteQuote: string | undefined;
    editNote: boolean;
    setEditNote: (val: boolean) => void;
    setNoteQuote: (val: string) => void;
    quote: QuoteProps;
}> = ({ noteQuote, editNote, setEditNote, setNoteQuote, quote }) => (
    <Card
        title="Quoting Notes"
        extra={
            !editNote ? (
                <div style={{ cursor: 'pointer', color: 'red' }} onClick={() => setEditNote(true)} className="mt-3">
                    <EditOutlined /> Edit
                </div>
            ) : (
                <div
                    style={{ cursor: 'pointer', color: 'red' }}
                    onClick={() => {
                        setEditNote(false);
                        QuoteStore.patchQuote(quote.id, { notes: noteQuote });
                    }}
                    className="mt-3"
                >
                    <SaveOutlined /> Save Changes
                </div>
            )
        }
    >
        <div className="flex flex-col">
            {editNote ? (
                <RichTextEditor value={noteQuote} onChange={html => setNoteQuote(html)} />
            ) : (
                <SafeHtml html={noteQuote ? noteQuote : quote?.notes} />
            )}
        </div>
    </Card>
);

const QuoteSummary: React.FC<{
    quote: QuoteProps;
    isCreatingADraftOrder: boolean;
    goCheckoutPage: () => void;
    isSafeToCheckout: boolean;
}> = ({ quote, isCreatingADraftOrder, goCheckoutPage, isSafeToCheckout }) => (
    <Card style={{ fontSize: '16px' }}>
        <div className="flex flex-row justify-between mb-1">
            <div className="summary-label">Subtotal</div>
            <div className="summary-value">
                <Price value={quote?.total_price ?? 0} />
            </div>
        </div>
        <div className="flex flex-row justify-between mb-1">
            <div className="summary-label">Application Cost:</div>
            <div className="summary-value">
                <Price value={quote?.application_cost ?? 0} />
            </div>
        </div>
        <div className="flex flex-row justify-between mt-2">
            <div className="summary-label">
                <div className="font-bold">Estimated Sales Tax:</div>
                <div className="text-xs">
                    (Zip Code: {quote?.shipping_zip_code}; Percentage: {quote.taxes_rate}%)
                </div>
            </div>
            <div className="summary-value">
                <Price value={quote?.taxes ?? 0} />
            </div>
        </div>
        <div className="flex flex-row justify-between mt-2 pt-4" style={{ borderTop: 'solid 1px #d9d9d9' }}>
            <div className="font-bold text-xl">Grand Total</div>
            <div className="text-xl">
                <Price value={quote?.grand_total ?? 0} />
            </div>
        </div>
        <div className="flex flex-row justify-end mt-4">
            <Tooltip
                title={
                    !QuoteStore.isSafeToCheckout
                        ? 'You cannot checkout because one or more items in your quote do not have valid prices. Please adjust quantities or remove items to proceed.'
                        : ''
                }
                placement="top"
            >
                <hr/>
                <Button
                    disabled={!QuoteStore.isSafeToCheckout}
                    type="primary"
                    size="large"
                    loading={isCreatingADraftOrder}
                    block
                    onClick={goCheckoutPage}

                >
                    Confirm and go to Checkout <ArrowRightOutlined />
                </Button>
            </Tooltip>
        </div>
    </Card>
);

const QuoteEdit: React.FC<IProps> = observer(({ match }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [isCreatingADraftOrder, setIsCreatingADraftOrder] = useState<boolean>(false);
    const [editNote, setEditNote] = useState<boolean>(false);
    const [noteQuote, setNoteQuote] = useState<string | undefined>('');
    const [showAllDynamicQuantitiesByItem, setShowAllDynamicQuantitiesByItem] = useState<number | null>(null);
    const [showRequestNewQuote, setShowRequestNewQuote] = useState<boolean>(false);
    const [quote, setQuote] = useState<QuoteProps | null>(null);
    const [contact, setContact] = useState<ContactDetailProps>();

    const [quote_item, setQuoteItem] = useState<QuoteLineItemProps | undefined>(undefined);

    const [parentLineItems, setParentLineItems] = useState<any[]>([]);
    const [taxRate, setTaxRate] = useState<number>(0);
    const [errors, setErrors] = useState<any>();

    const [showOriginalItems, setShowOriginalItems] = useState<boolean>(false);

    const history = useHistory();

    const { id } = match.params;

    const contact_id = UserStore.user?.default_contact?.id;

    const loadQuote = async (id: number) => {
        setLoading(true);
        await QuoteStore.getQuoteById(id);
        setQuote(QuoteStore.quote);
        setNoteQuote(QuoteStore.quote?.notes);

        setLoading(false);
    };
    useEffect(() => {
        if (contact_id) {
            loadContact(contact_id);
        }
    }, [contact_id]);

    const loadContact = async (id: number) => {
        const _contact = await api.User.getContact(id);
        setContact(_contact);
    };

    async function loadLineItems(id: number) {
        await QuoteStore.getLineItems(id);
        QuoteStore.line_items.map(item => {
            if (item.has_dynamic_quantities) {
                const dynamics = item.quote_item_quantities.filter(
                    (dynamic: { displayed: boolean }) => dynamic.displayed
                );
                if (dynamics.length === 0) {
                    QuoteStore.line_items.splice(
                        QuoteStore.line_items.findIndex((_item: { id: number }) => _item.id === item.id),
                        1
                    );
                }
            }
        });
    }

    async function loadParentLineItems(quote_id: number) {
        setLoading(true);
        await QuoteStore.loadParentLineItems(quote_id);
        QuoteStore.parent_line_items.map(
            (item: { has_dynamic_quantities: boolean; quote_item_quantities: any[]; id: number }) => {
                if (item.has_dynamic_quantities) {
                    const dynamics = item.quote_item_quantities.filter(
                        (dynamic: { displayed: number }) => dynamic.displayed
                    );
                    if (dynamics.length === 0) {
                        QuoteStore.parent_line_items.splice(
                            QuoteStore.parent_line_items.findIndex((_item: { id: any }) => _item.id === item.id),
                            1
                        );
                    }
                }
            }
        );
        const parent_filter: any = _(QuoteStore.parent_line_items)
            .differenceBy(QuoteStore.line_items, 'name')
            .map(_.partial(_.pick, _, 'id', 'name', 'product'))
            .value();
        setParentLineItems(parent_filter);
        setLoading(false);
    }

    /**
     * Load Quote by id
     */
    useEffect(() => {
        UIStore.setSelectedKey(['quotes']);
        UIStore.setDocumentTitle('Ordering Quote');
        if (id) {
            loadQuote(id);
            loadLineItems(id);
        }
    }, []);

    /**
     *  Get Taxes with shipping zip code
     */
    useEffect(() => {
        if (contact_id && quote?.shipping_zip_code) {
            api.Contacts.getTaxRate(contact_id, quote?.shipping_zip_code)
                .then(data => {
                    setTaxRate(data?.estimated_combined_rate);
                })
                .catch(err => {
                    console.log(err);
                });
        }
    }, [contact_id, quote?.shipping_zip_code]);

    const goCheckoutPage = async () => {
        const contact_id = UserStore.user?.default_contact?.id;

        if (!contact_id) {
            message.error('Something went wrong with your account. Please contact support. CODE: CONTACT_NOT_FOUND');
            return;
        }

        setIsCreatingADraftOrder(true);

        type IOrder = {
            quantity: number;
            name: string;
            quote_has_product?: number | undefined;
            product_description: string;
            unit_price: string;
            list_price: string;
        };

        let order_items: IOrder[] = [];

        QuoteStore.getQuoteLineItems
            .filter(i => !i.has_dynamic_quantities)
            .forEach(item => {
            order_items = [
                {
                name: item.name,
                product_description: '',
                quote_has_product: item.id,
                quantity: item.quantity,
                unit_price: item.unit_price,
                list_price: item.unit_price,
                },
                ...order_items,
            ];
            });

        QuoteStore.getQuoteLineItems
            .filter(i => i.has_dynamic_quantities)
            .forEach(item => {
            order_items = [
                ...item.quote_item_quantities
                .filter((i: { displayed: any }) => i.displayed)
                .map((quantity_item: { quantity: any; unit_price: any }) => ({
                    name: item.name,
                    product_description: '',
                    quote_has_product: item.id,
                    quantity: quantity_item.quantity,
                    unit_price: quantity_item.unit_price,
                    list_price: item.unit_price,
                })),
                ...order_items,
            ];
            });

        const application_cost_items: IOrder[] = quote?.application_cost_items?.length
            ? quote.application_cost_items.map((item: AdditionalRequirementsProps) => ({
                name: 'CNC Part',
                product_description: item.additional_requirement,
                quote_has_product: undefined,
                quantity: 1,
                unit_price: item.price,
                list_price: item.price,
            }))
            : [];

        const payload = {
            contact: contact_id,
            line_items: [...order_items, ...application_cost_items],
        };

        try {
            const order = await SalesOrderStore.createOrderDraft(payload);
            history.push(`/my-account/orders/checkout/${order.id}`);
        } catch (error: any) {
            notification.error({
            duration: 10,
            key: 'go-checkout-page-error',
            message: 'Error',
            description: <ErrorRender error={error} />,
            });
        } finally {
            setIsCreatingADraftOrder(false);
        }
    };

    const line_items_by_part = _.groupBy(QuoteStore.line_items, item => {
        return item?.product?.master_product;
    });

    if (!quote?.id) return <PortalLoading />;

    return (
        <>
            <QuoteDetailHeader
                quote={quote}
                quote_line_items={QuoteStore.line_items}
                editQuote={true}
                loadParentLineItems={() => {
                    if (quote?.duplicated_from) {
                        loadParentLineItems(quote?.duplicated_from);
                        setShowOriginalItems(true);
                    }
                }}
                loading={loading}
                goCheckoutPage={() => {
                    goCheckoutPage();
                }}
                parentLineItems={parentLineItems}
            />

            <div className="container">
                <>
                    <BreadcrumbsNav quote={quote} />

                    <Row>
                        <Col span={24}>
                            <Alert
                                type="info"
                                showIcon={true}
                                message="Modifying Quote"
                                description="You may change quantities or remove products to match your Purchase Order. Please note that only parts that were quoted originally are included."
                                className="mb-3"
                            />
                        </Col>
                    </Row>

                    {QuoteStore.line_items.length > 0 && (
                        <>
                            <Row gutter={[16, 8]}>
                                <Col xl={16} span={24}>
                                    <Spin spinning={loading}>
                                        {_.map(line_items_by_part, (_line_items: QuoteLineItemProps[], key) => {
                                            const first_part = _line_items[0];
                                            return (
                                                <LineItemsGroup
                                                    key={key}
                                                    quote={quote}
                                                    firstPart={first_part}
                                                    items={_line_items}
                                                    // loadingPriceOptions={loadingPriceOptions}
                                                    // onRemove={handleRemove}
                                                    // onChangeQty={handleChangeQuantity}
                                                    // onChangePrice={handleUpdateItemPrice}
                                                    // onRequote={sendToRequote}
                                                    setQuote={setQuote}
                                                    loadLineItems={loadLineItems}
                                                    setQuoteItem={setQuoteItem}
                                                    setShowAllDynamicQuantitiesByItem={
                                                        setShowAllDynamicQuantitiesByItem
                                                    }
                                                    setShowRequestNewQuote={setShowRequestNewQuote}
                                                    loadQuote={loadQuote}
                                                    setLoading={setLoading}
                                                />
                                            );
                                        })}
                                    </Spin>

                                    {getCurrentUser().data?.is_staff && (
                                        <QuoteNotes
                                            noteQuote={noteQuote}
                                            editNote={editNote}
                                            setEditNote={setEditNote}
                                            setNoteQuote={setNoteQuote}
                                            quote={quote}
                                        />
                                    )}

                                    {quote?.application_cost_items?.length > 0 && (
                                        <Col lg={24}>
                                            <Card
                                                className={'flex flex-col justify-between'}
                                                style={{ width: '100%', fontSize: '16px' }}
                                            >
                                                <AdditionalRequirements
                                                    quote_id={quote?.id}
                                                    editable={quote?.editable}
                                                    loadQuote={() => loadQuote(quote.id)}
                                                    additional_requirements={quote.application_cost_items}
                                                />
                                            </Card>
                                        </Col>
                                    )}
                                </Col>

                                <Col xl={8} span={24}>
                                    <QuoteSummary
                                        quote={quote}
                                        isCreatingADraftOrder={isCreatingADraftOrder}
                                        goCheckoutPage={goCheckoutPage}
                                        isSafeToCheckout={true}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}

                    {errors && <ErrorRender error={errors} />}

                    <Drawer
                        title="Add Original Line Items"
                        placement="right"
                        closable={true}
                        width={'640px'}
                        onClose={() => {
                            setShowOriginalItems(false);
                        }}
                        open={showOriginalItems}
                    >
                        <List
                            key={'original-items'}
                            itemLayout="horizontal"
                            loading={loading}
                            dataSource={parentLineItems}
                            renderItem={(item: any) => (
                                <List.Item
                                    actions={[
                                        <Button
                                            className="ant-btn-xs"
                                            key="add-item"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                api.Quotes.addLineItem(quote.id, {
                                                    id: item.id,
                                                })
                                                    .then(() => {
                                                        loadLineItems(quote.id).then(() => {
                                                            setParentLineItems(QuoteStore.filterParentLineItems);
                                                            loadQuote(quote.id);
                                                        });
                                                        message.success('Item added to quote');
                                                    })
                                                    .catch(error => {
                                                        console.error(error);
                                                    });
                                            }}
                                        >
                                            Add To Quote
                                        </Button>,
                                    ]}
                                >
                                    <Skeleton avatar title={false} loading={item.loading} active>
                                        <List.Item.Meta
                                            key={item.id}
                                            title={
                                                <div className="d-flex flex-row align-items-center">
                                                    <div className="mr-4">
                                                        <PartPreview
                                                            thumbnail={item.product.attachments?.file3d?.image}
                                                            has_file_3d={item.product?.has_file_3d}
                                                            width="85px"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Title level={4}>{item.name}</Title>
                                                        <div className={'dimensions'}>
                                                            <PartDimensions {...item?.product?.dimensions_dict} />
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        />
                                    </Skeleton>
                                </List.Item>
                            )}
                        />
                    </Drawer>

                    <Modal
                        title={'Request New Quantity'}
                        centered
                        open={showRequestNewQuote}
                        onCancel={() => setShowRequestNewQuote(false)}
                        footer={false}
                    >
                        <RequestNewQuote
                            quote={{
                                id: quote.id,
                                name: quote.name,
                            }}
                            item={quote_item}
                            onCloseModal={(value: boolean) => setShowRequestNewQuote(value)}
                        />
                    </Modal>
                </>

                {showAllDynamicQuantitiesByItem && (
                    <AllDynamicQuantities
                        quote_id={quote.id}
                        item_id={showAllDynamicQuantitiesByItem}
                        handleClosePrices={() => setShowAllDynamicQuantitiesByItem(null)}
                        editable={true}
                        requestNewQuote={() => {
                            setShowAllDynamicQuantitiesByItem(null);
                            setShowRequestNewQuote(true);
                        }}
                        handleSelect={async qty => {
                            setLoading(true);
                            QuoteStore.addDynamicVariation(quote.id, showAllDynamicQuantitiesByItem, qty)
                                .then(() => {
                                    loadLineItems(id);
                                    loadQuote(quote.id);
                                    setShowAllDynamicQuantitiesByItem(null);
                                })
                                .finally(() => setLoading(false));
                        }}
                    />
                )}
            </div>
        </>
    );
});

export default QuoteEdit;
